import Cookies from 'js-cookie';

import type { Viewer } from '@vimeo/viewer';

/**
 * Returns the viewer's authentication status for analytics events.
 * 'logged_out' → not authenticated, 'free' → free/basic account, 'paid' → any paid plan.
 *
 * For team members, viewer.user.account is always 'basic' regardless of the team plan.
 * We use teamUser.accountType when present (the team owner's plan), falling back to
 * user.account for non-team users — the same pattern used in extractSafeViewerInfo
 * and Pendo initialisation throughout the codebase.
 */
export function deriveViewerAuthStatus(viewer: Viewer | null): 'logged_out' | 'free' | 'paid' {
  if (!viewer?.user) return 'logged_out';
  const effectiveAccount = viewer.teamUser?.accountType ?? viewer.user.account;
  return ['free', 'basic'].includes(effectiveAccount) ? 'free' : 'paid';
}

/**
 * Canonical labels for Vimeo pages, used for analytics page categorisation.
 * Keep values snake_case to match PicoX payload conventions.
 *
 * Derived from URL (and where required, viewer info) by `deriveCanonicalPage`.
 * Library-specific values (`my_library`, `team_library`, `shared_with_me`)
 * intentionally match those returned by `deriveLibraryType` so the two helpers
 * can be used interchangeably at call sites that already have folder context.
 */
export type CanonicalPage =
  | 'search'
  | 'clip_page'
  | 'user_profile'
  | 'homepage'
  | 'watchpage'
  | 'staff_picks'
  | 'category_page'
  | 'my_library'
  | 'team_library'
  | 'shared_with_me'
  | 'editor'
  | 'unknown';

/**
 * Possible values returned by `deriveReferrerPage`. Adds `'external'` to the
 * canonical set for off-vimeo referrers.
 */
export type ReferrerPage = CanonicalPage | 'external';

/**
 * Pure URL-to-page-label mapper.
 *
 * `viewer.is_team_user` is required to disambiguate `/library*` URLs:
 * the same path renders as personal library for solo users and as team
 * library for team members. When no viewer is available (e.g. when called
 * from a referrer-only context), the `/library*` case falls through to
 * `'unknown'` rather than guessing.
 */
export function deriveCanonicalPage(
  url: URL,
  viewer?: { is_team_user?: boolean | null },
): CanonicalPage {
  const path = url.pathname;
  const isPrivate = url.searchParams.get('isPrivate') === 'true';

  if (/^\/search/.test(path)) return 'search';
  if (/^\/staff_picks/.test(path)) return 'staff_picks';
  if (/^\/categories/.test(path)) return 'category_page';
  if (/^\/watch\/?$/.test(path)) return 'watchpage';
  if (path === '/') return 'homepage';
  if (/^\/home\/?$/.test(path)) return 'homepage';
  if (/^\/create\/edit(\/|$)/.test(path)) return 'editor';
  if (/^\/shared-with-me(\/|$)/.test(path)) return 'shared_with_me';
  if (/^\/user\/[^/]+\/folder\//.test(path)) return isPrivate ? 'my_library' : 'team_library';
  if (/^\/library(\/|$)/.test(path)) {
    if (viewer?.is_team_user == null) return 'unknown';
    return viewer.is_team_user ? 'team_library' : 'my_library';
  }
  if (/^\/\d+\/?$/.test(path)) return 'clip_page';
  if (/^\/[^/]+\/?$/.test(path)) return 'user_profile';
  return 'unknown';
}

/**
 * Categorises `document.referrer` for analytics. Returns `'external'` for
 * referrers outside vimeo.com, `'unknown'` when there's no referrer or the
 * URL can't be parsed. Otherwise delegates to `deriveCanonicalPage`.
 *
 * Note: viewer info isn't passed in the referrer flow, so `/library*`
 * referrers fall through to `'unknown'` rather than guessing solo vs team.
 */
export function deriveReferrerPage(): ReferrerPage {
  if (typeof document === 'undefined' || !document.referrer) return 'unknown';
  try {
    const url = new URL(document.referrer);
    if (url.hostname !== 'vimeo.com' && !url.hostname.endsWith('.vimeo.com')) return 'external';
    return deriveCanonicalPage(url);
  } catch {
    return 'unknown';
  }
}

export type LibraryType = 'team_library' | 'my_library' | 'shared_with_me';

/**
 * Derives the library_type value for PicoX tracking from page context.
 *
 * Rules:
 *  - isSharedWithMe=true → 'shared_with_me'
 *  - isPrivateToUser=true → 'my_library'  (the private/personal root folder for team users)
 *  - isPrivateToUser=false → 'team_library' (a folder within the team library)
 *  - /library route (no isPrivateToUser):
 *      hasContentSpaceEnabled=true  → 'team_library'
 *      hasContentSpaceEnabled=false → 'my_library'  (solo/individual user)
 */
export function deriveLibraryType(params: {
  hasContentSpaceEnabled: boolean;
  isPrivateToUser?: boolean | null;
  isSharedWithMe?: boolean;
}): LibraryType {
  if (params.isSharedWithMe) return 'shared_with_me';
  if (params.isPrivateToUser === true) return 'my_library';
  if (params.isPrivateToUser === false) return 'team_library';
  return params.hasContentSpaceEnabled ? 'team_library' : 'my_library';
}

export function deriveLibraryReferrerPage(
  sidebarParam?: string | string[],
): 'homepage' | 'sidebar' | 'external' | 'unknown' {
  if (sidebarParam === 'sidebar') return 'sidebar';
  if (typeof document === 'undefined' || !document.referrer) return 'unknown';
  try {
    const url = new URL(document.referrer);
    if (url.hostname !== 'vimeo.com' && !url.hostname.endsWith('.vimeo.com')) return 'external';
    const path = url.pathname;
    if (path === '/' || path === '/home' || path === '/home/') return 'homepage';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

import type { PicoContextData, PicoIdentifiers } from './pico';

/**
 * Cookie set by the backend (`Vimeo\Cookie::COOKIE_PAYMENT_FAILED_MODAL`) when
 * the current user is in payment grace period. Value is `'1'` for grace period;
 * other values represent unrelated payment-failure states.
 *
 * Same source of truth used by Pendo (see
 * `frontend/framework/core/libs/pendo/initialize-pendo.ts`).
 */
const IS_IN_GRACE_PERIOD_COOKIE = 'is_in_grace_period';

/**
 * Returns whether the current user is in payment grace period, derived from
 * the backend-managed `is_in_grace_period` cookie. SSR-safe: returns `false`
 * when no document is available.
 */
export function deriveIsInGracePeriod(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  return Cookies.get(IS_IN_GRACE_PERIOD_COOKIE) === '1';
}

// `is_in_grace_period` is sourced from a cookie (not the viewer), so it is
// omitted from SafeViewerInfo and populated separately in PicoProvider.
export type SafeViewerInfo = PicoIdentifiers & Omit<PicoContextData, 'is_in_grace_period'>;
/**
 * Extracts safe viewer info from a Viewer object.
 * Returns an object containing the necessary information to initialize PicoX.
 *
 * @example
 * const {
 *   viewerId, vuid, teamId, teamOwnerId, organizationId,
 *   accountType, isTeamUser, isFreeTrial, country, isMobile
 * } = extractSafeViewerInfo(viewer);
 */
export function extractSafeViewerInfo(viewer: Viewer | null): SafeViewerInfo {
  if (!viewer) {
    return {
      user_id: null,
      vuid: null,
      team_id: null,
      team_owner_id: null,
      actor_id: null,
      organization_id: null,
      account_type: null,
      is_team_user: false,
      is_free_trial: false,
      country: null,
      is_mobile: false,
    };
  }

  const user_id = viewer.user?.id?.toString() ?? null;
  const vuid = viewer.vuid;
  const team_id = viewer.teamUser?.teamId?.toString() ?? null;
  const team_owner_id = viewer.teamUser?.ownerId?.toString() ?? null;
  const actor_id = team_owner_id ? `T_${team_owner_id}` : user_id ? `U_${user_id}` : null;
  const organization_id = viewer.user?.organizationId ?? null;
  const account_type =
    viewer.teamUser?.accountType?.toString() ?? viewer.user?.account?.toString() ?? null;
  const is_team_user = viewer.user?.isTeamUser ?? false;
  const is_free_trial = viewer.user?.isFreeTrial ?? false;
  const country = viewer.location;
  const is_mobile = viewer.isMobile;

  return {
    user_id,
    vuid,
    team_id,
    team_owner_id,
    actor_id,
    organization_id,
    account_type,
    is_team_user,
    is_free_trial,
    country,
    is_mobile,
  };
}
